import { motion } from "framer-motion";
import { Check, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const PricingSection = ({ onOpenModal }) => {
  const features = [
    "Unlimited customers",
    "Unlimited rewards",
    "QR code generation",
    "Customer analytics dashboard",
    "Smart notifications",
    "Multi-device access",
    "Email support",
    "No setup fees",
  ];

  return (
    <section 
      id="pricing"
      className="section-padding bg-white"
      data-testid="pricing-section"
    >
      <div className="container-main">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-50 text-amber-700 text-sm font-medium mb-4 border border-amber-200">
            Simple Pricing
          </span>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="pricing-headline"
          >
            Grow Together Model
          </h2>
          <p className="text-lg text-slate-600">
            We only succeed when you succeed. No monthly fees, no hidden costs.
          </p>
        </motion.div>

        {/* Pricing card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto"
        >
          <div className="pricing-highlight bg-white rounded-3xl p-8 lg:p-10 border-2 border-indigo-100 shadow-xl shadow-indigo-100/50">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                <span>Win-Win Pricing</span>
              </div>
              
              <div className="mb-4">
                <span className="text-6xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Free
                </span>
                <span className="text-xl text-slate-500 ml-2">to start</span>
              </div>
              
              <div className="flex items-center justify-center gap-3">
                <div className="h-px w-12 bg-slate-200" />
                <span className="text-sm text-slate-500">then</span>
                <div className="h-px w-12 bg-slate-200" />
              </div>
              
              <div className="mt-4">
                <span className="text-4xl font-bold gradient-text" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  3%
                </span>
                <span className="text-lg text-slate-600 ml-2">per transaction</span>
              </div>
            </div>

            {/* Description */}
            <div className="bg-slate-50 rounded-2xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">The Fair Deal</h4>
                  <p className="text-sm text-slate-600">
                    When your customers earn points through us, you pay 3% of that transaction. 
                    No sales = no fees. Your business grows, we grow together.
                  </p>
                </div>
              </div>
            </div>

            {/* Features list */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-emerald-600" />
                  </div>
                  <span className="text-slate-700">{feature}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <Button
              onClick={onOpenModal}
              className="w-full btn-primary h-14 text-lg"
              data-testid="pricing-cta-btn"
            >
              Start Growing Free
            </Button>

            {/* Trust note */}
            <p className="text-center text-sm text-slate-500 mt-4">
              No credit card required • Cancel anytime
            </p>
          </div>
        </motion.div>

        {/* FAQ snippet */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-12 text-center"
        >
          <p className="text-slate-600">
            Questions? <a href="#contact" className="text-indigo-600 font-medium hover:text-indigo-700">Contact us</a> or check our FAQ
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
